import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  parseMaxFromLimitText,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const ChemicalReportDetail: React.FC<{
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, metadata, projectLimitFields = [], frequency, measurements = [], deletedMeasurementIds = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const chemicalMeasurements = measurements.filter((measurement) => measurement.reportType === 'CHEMICAL');
  const getChemicalMeasurementSymbol = (measurement: ReportMeasurementItem) =>
    measurement.fieldSymbol || measurement.fieldPath.split('.').pop() || measurement.fieldLabel;
  const toMeasurementElement = (measurement: ReportMeasurementItem): ChemicalElementDisplay => ({
    id: measurement.id,
    symbol: getChemicalMeasurementSymbol(measurement),
    name: measurement.fieldLabel,
    value: Number(measurement.measuredValue) || 0,
    displayValue: measurement.measuredValue || '-',
    unit: measurement.unit,
    limit: getProjectLimitText(projectLimitFields, measurement.fieldPath, measurement.limit),
    max: getProjectLimitField(projectLimitFields, measurement.fieldPath)?.max ?? undefined,
    status: measurement.status,
    source: 'user',
    measurement,
  });
  const measurementElements: ChemicalElementDisplay[] = chemicalMeasurements.map(toMeasurementElement);
  const elements: ChemicalElementDisplay[] = measurementElements;
  const getElementValue = (symbol: string): number | null => {
    const element = elements.find((item) => item.symbol.toLowerCase() === symbol.toLowerCase());
    const value = Number(element?.displayValue ?? element?.value);
    return Number.isFinite(value) ? value : null;
  };
  const getMissingElements = (symbols: string[]): string[] =>
    symbols.filter((symbol) => getElementValue(symbol) == null);
  const calculateWhenAvailable = (symbols: string[], calculate: (values: Record<string, number>) => number): number | null => {
    const values = symbols.reduce((acc, symbol) => {
      const value = getElementValue(symbol);
      return value == null ? acc : { ...acc, [symbol]: value };
    }, {} as Record<string, number>);
    return symbols.every((symbol) => values[symbol] != null) ? calculate(values) : null;
  };
  const getFormulaLimitMax = (fieldKeys: string[]): number | null => {
    const normalizedKeys = fieldKeys.map((key) => key.toLowerCase());
    const limit = projectLimitFields.find((field) => {
      const candidates = [field.fieldPath, field.symbol, field.label].filter(Boolean).map((value) => String(value).toLowerCase());
      return candidates.some((candidate) => normalizedKeys.includes(candidate));
    });
    return limit?.max ?? null;
  };
  const derivedCalculations = [
    {
      key: 'V_NB_TI',
      label: 'V + Nb + Ti sum',
      formula: 'V + Nb + Ti',
      missing: getMissingElements(['V', 'Nb', 'Ti']),
      value: calculateWhenAvailable(['V', 'Nb', 'Ti'], ({ V, Nb, Ti }) => V + Nb + Ti),
      max: getFormulaLimitMax(['V_NB_TI', 'v_nb_ti', 'microalloy_sum', 'V + Nb + Ti sum']),
    },
    {
      key: 'CE_IIW',
      label: 'Carbon equivalent',
      formula: 'C + Mn/6 + (Cr+Mo+V)/5 + (Ni+Cu)/15',
      missing: getMissingElements(['C', 'Mn', 'Cr', 'Mo', 'V', 'Ni', 'Cu']),
      value: calculateWhenAvailable(['C', 'Mn', 'Cr', 'Mo', 'V', 'Ni', 'Cu'], ({ C, Mn, Cr, Mo, V, Ni, Cu }) => C + Mn / 6 + (Cr + Mo + V) / 5 + (Ni + Cu) / 15),
      max: getFormulaLimitMax(['CE_IIW', 'CEQ_IIW', 'ce_iiw', 'ceq_iiw', 'ceq_IIW', 'Carbon equivalent', 'Carbon equivalent IIW']),
    },
    {
      key: 'Pcm',
      label: 'Weld cracking idx',
      formula: 'C + Si/30 + (Mn+Cu+Cr)/20 + Ni/60 + Mo/15 + V/10 + 5B',
      missing: getMissingElements(['C', 'Si', 'Mn', 'Cu', 'Cr', 'Ni', 'Mo', 'V', 'B']),
      value: calculateWhenAvailable(['C', 'Si', 'Mn', 'Cu', 'Cr', 'Ni', 'Mo', 'V', 'B'], ({ C, Si, Mn, Cu, Cr, Ni, Mo, V, B }) => C + Si / 30 + (Mn + Cu + Cr) / 20 + Ni / 60 + Mo / 15 + V / 10 + 5 * B),
      max: getFormulaLimitMax(['Pcm', 'PCM', 'pcm', 'Weld cracking idx', 'Weld cracking index']),
    },
  ].map((calculation) => ({
    ...calculation,
    status: calculation.value == null || calculation.max == null ? 'PENDING_REVIEW' : calculation.value <= calculation.max ? 'PASS' : 'FAIL',
  }));
  const editChemicalElement = (element: ChemicalElementDisplay) => {
    onEditMeasurement?.(element.measurement || {
      id: `chemical-${element.symbol}`,
      reportType: 'CHEMICAL',
      fieldPath: `elements.${element.symbol}`,
      fieldLabel: element.name,
      fieldSymbol: element.symbol,
      measuredValue: element.displayValue,
      unit: element.unit,
      frequency: 'per_heat',
      limit: element.limit,
      source: 'Manual entry',
      reference: CHEMICAL_DETAIL.sourceFile,
      status: element.status,
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        {/* <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <StatCard label="Heat" value={heatNumber} sub="covered by report" />
          <StatCard label="Elements" value={elements.length} sub={`${chemicalMeasurements.length} added`} status="PASS" />
          <StatCard label="CE IIW" value={derivedCalculations[1].value == null ? '-' : derivedCalculations[1].value.toFixed(2)} sub={derivedCalculations[1].max == null ? 'limit not configured' : `<= ${derivedCalculations[1].max}`} status={derivedCalculations[1].status} />
          <StatCard label="Pcm" value={derivedCalculations[2].value == null ? '-' : derivedCalculations[2].value.toFixed(2)} sub={derivedCalculations[2].max == null ? 'limit not configured' : `<= ${derivedCalculations[2].max}`} status={derivedCalculations[2].status} />
        </div> */}

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <div className="flex flex-col gap-3 border-b border-[#E2E5EA] bg-white px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Periodic-table view</h3>
                <p className="mt-1 text-[12px] text-heritage-grey">
                  {elements.length} measured elements - {chemicalMeasurements.length} user-added items
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="rounded-lg border border-[#E2E5EA] bg-page-bg p-1">
                  {(['table', 'grid'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setViewMode(mode)}
                      className={cn(
                        'rounded-md px-3 py-1 text-[11px] font-semibold capitalize',
                        viewMode === mode ? 'bg-white text-blueprint-blue shadow-sm' : 'text-heritage-grey'
                      )}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
                <Button type="button" size="sm" onClick={() => onAddMeasurement?.('CHEMICAL')} className="bg-hero-red text-white hover:bg-hero-red-60">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Item
                </Button>
              </div>
            </div>

            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 gap-3 bg-page-bg p-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                {elements.map((element) => {
                  const maxLimit = element.max ?? parseMaxFromLimitText(element.limit);
                  const isFail = element.status === 'FAIL' || (maxLimit != null && element.value > maxLimit);
                  const progressWidth = `${Math.min(100, Math.max(8, ((element.value || 0) / ((maxLimit || element.value || 1) * 1.15)) * 100))}%`;
                  const limitMargin = maxLimit ? Math.max(0, (1 - element.value / maxLimit) * 100).toFixed(0) : null;

                  return (
                    <div
                      key={`${element.source}-${element.id}`}
                      onClick={() => editChemicalElement(element)}
                      className={cn(
                        'min-h-[118px] cursor-pointer rounded-xl border bg-white p-4 text-left shadow-sm transition hover:shadow-md',
                        isFail ? 'border-readable-red/40 bg-[rgba(196,0,31,0.04)] hover:border-readable-red' : 'border-[#E2E5EA] hover:border-hero-red-20'
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <span className={cn('font-heading text-[18px] font-semibold', isFail ? 'text-readable-red' : 'text-blueprint-blue')}>{element.symbol}</span>
                          <span className="ml-2 text-[11px] text-heritage-grey">{element.name}</span>
                        </div>
                        <span className={cn('mt-1 h-1.5 w-1.5 rounded-full', isFail ? 'bg-readable-red' : element.status === 'PENDING_REVIEW' ? 'bg-[#B38600]' : 'bg-galvanizing-green')} />
                      </div>
                      <div className="mt-4 flex items-end justify-between gap-2">
                        <div className={cn('font-mono text-[18px] font-semibold', isFail ? 'text-readable-red' : 'text-blueprint-blue')}>
                          {element.displayValue}
                          {element.unit && <span className="ml-1 text-[11px] font-normal text-heritage-grey">{element.unit}</span>}
                        </div>
                        <div className="font-mono text-[10px] text-heritage-grey">{element.storeOnly ? 'store-only' : element.limit}</div>
                      </div>
                      {element.storeOnly ? (
                        <div className="mt-3 text-[10px] italic text-heritage-grey">store-only - no limit</div>
                      ) : (
                        <div className={cn('mt-3 h-2 overflow-hidden rounded-full', isFail ? 'bg-[rgba(196,0,31,0.12)]' : 'bg-[#E8F3EF]')}>
                          <div
                            className={cn('h-full rounded-full', isFail ? 'bg-readable-red' : 'bg-galvanizing-green')}
                            style={{ width: progressWidth }}
                          />
                        </div>
                      )}
                      <div className="mt-2 flex items-center justify-between gap-2 text-[10px] text-heritage-grey">
                        <span>
                          {isFail
                            ? 'limit failed'
                            : element.itpStricter && element.stdMax
                              ? `ITP stricter than std <= ${element.stdMax}`
                              : limitMargin
                                ? `${limitMargin}% below limit`
                                : 'within limit'}
                        </span>
                        <span className="flex items-center gap-1">
                          <button
                            type="button"
                            title="Edit"
                            onClick={(event) => {
                              event.stopPropagation();
                              editChemicalElement(element);
                            }}
                            className="rounded p-1 font-semibold text-blueprint-blue hover:bg-page-bg"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          {element.measurement && (
                            <button
                              type="button"
                              title="Delete"
                              onClick={(event) => {
                                event.stopPropagation();
                                onDeleteMeasurement?.(element.measurement!);
                              }}
                              className="rounded p-1 font-semibold text-readable-red hover:bg-[rgba(196,0,31,0.06)]"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <table className="w-full text-left">
                <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                  <tr>
                    <th className="px-4 py-3">Element</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3 text-right">Measured</th>
                    <th className="px-4 py-3 text-right">Limit</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                  {elements.map((element) => (
                    <tr key={`${element.source}-${element.id}`} onClick={() => editChemicalElement(element)} className="cursor-pointer hover:bg-page-bg">
                      <td className="px-4 py-3 font-mono font-semibold">{element.symbol}</td>
                      <td className="px-4 py-3">{element.name}</td>
                      <td className="px-4 py-3 text-right font-mono">{element.displayValue} {element.unit}</td>
                      <td className="px-4 py-3 text-right font-mono">{element.storeOnly ? 'store-only' : element.limit}</td>
                      <td className="px-4 py-3"><StatusBadge status={element.status} /></td>
                      <td className="px-4 py-3 text-right">
                        {element.measurement && (
                          <ItemActionButtons measurement={element.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

      <Card className="border-[#E2E5EA]">
        <CardContent className="p-0">
          <SectionHead title="Derived & Formula Fields" subtitle="Calculated from saved chemical items only" />
          {derivedCalculations.map((calculation) => (
            <ChemicalFormulaRow
              key={calculation.key}
              label={calculation.label}
              formula={calculation.formula}
              value={calculation.value}
              max={calculation.max}
              missing={calculation.missing}
              status={calculation.status}
            />
          ))}
        </CardContent>
      </Card>

      {/* <Card className="border-[#E2E5EA]">
        <CardContent className="p-0">
          <SectionHead title="Heat & Coverage" subtitle="One chemical analysis covers every pipe sharing the heat number" />
          <div className="grid grid-cols-1 gap-0 divide-y divide-[#EEF0F3] p-4 lg:grid-cols-[160px_130px_150px_minmax(0,1fr)] lg:divide-x lg:divide-y-0">
            <div className="px-0 py-3 lg:px-4 lg:py-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Heat Number</div>
              <div className="mt-2 font-heading text-[18px] font-semibold text-blueprint-blue">{CHEMICAL_DETAIL.coverage.heatNumber}</div>
              <div className="mt-1 text-[11px] text-heritage-grey">{CHEMICAL_DETAIL.coverage.mill} - cast {CHEMICAL_DETAIL.coverage.castDate}</div>
            </div>
            <div className="px-0 py-3 lg:px-4 lg:py-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Pipes Covered</div>
              <div className="mt-2 font-heading text-[18px] font-semibold text-blueprint-blue">{CHEMICAL_DETAIL.coverage.pipesCovered}</div>
              <div className="mt-1 text-[11px] text-heritage-grey">Pipe {CHEMICAL_DETAIL.coverage.pipeNo} is one of them</div>
            </div>
            <div className="px-0 py-3 lg:px-4 lg:py-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Analysis Type</div>
              <div className="mt-2 font-heading text-[18px] font-semibold text-blueprint-blue">{CHEMICAL_DETAIL.coverage.analysisType}</div>
              <div className="mt-1 text-[11px] text-heritage-grey">{CHEMICAL_DETAIL.coverage.note}</div>
            </div>
            <div className="px-0 py-3 lg:px-4 lg:py-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Pipes In Heat (First 24 Shown)</div>
              <div className="mt-3 grid grid-cols-12 gap-1 sm:grid-cols-[repeat(24,minmax(0,1fr))]">
                {Array.from({ length: 24 }).map((_, index) => (
                  <div
                    key={index}
                    title={`Pipe ${30850 + index}`}
                    className={cn(
                      'h-4 rounded-sm border border-[#CDEAD9]',
                      index === CHEMICAL_DETAIL.coverage.highlightedIndex ? 'bg-blueprint-blue' : 'bg-[#EAF8EF]'
                    )}
                  />
                ))}
              </div>
              <div className="mt-2 text-[11px] text-heritage-grey">highlighted = pipe {CHEMICAL_DETAIL.coverage.pipeNo}</div>
            </div>
          </div>
        </CardContent>
      </Card> */}
    </main>

    <ReportMetadataRail
      rows={[
        ['Report ID', metadata?.reportId || '-', true],
        ['Performed at', formatDisplayDate(metadata?.performedAt), true],
        ['Lab', metadata?.lab || '-'],
        ['Technician', metadata?.technician || '-'],
        ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
        ['Source file', metadata?.sourceFile || '-', true],
      ]}
      extraTitle="Limits In Effect"
      extraRows={getProjectLimitRows(projectLimitFields, [
        ['No project limits', 'Configure project limits to show live values', true],
      ])}
    >
      <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
      <Card className="mt-3 border-[#E2E5EA]">
        <CardContent className="p-4">
          <KeyValueRows
            rows={[
              ['Frequency', metadata?.frequency || frequency || '-', true],
              ['Heat number', heatNumber || '-', true],
              ['Scope source', frequency ? 'frequency_rules API' : 'not configured'],
            ]}
          />
        </CardContent>
      </Card>
      <AdjudicationPanel />
    </ReportMetadataRail>
  </div>
  );
};
